import { Link } from 'react-router-dom'
import { useState, FormEvent } from 'react'
import { scrollToFirstError } from '@/lib/form'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

interface FieldErrors {
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  confirmPassword?: string
  terms?: string
}

export default function InscriptionPrestatairePage() {
  const [showPwd, setShowPwd] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [success, setSuccess] = useState(false)

  function validate(fd: FormData): FieldErrors {
    const errs: FieldErrors = {}
    const firstName = fd.get('firstName') as string
    const lastName = fd.get('lastName') as string
    const email = fd.get('email') as string
    const password = fd.get('password') as string
    const confirmPassword = fd.get('confirmPassword') as string
    const terms = fd.get('terms')

    if (!firstName || firstName.trim().length < 2) errs.firstName = 'Prénom requis'
    if (!lastName || lastName.trim().length < 2) errs.lastName = 'Nom requis'
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Adresse email invalide'
    if (!password || password.length < 8) errs.password = 'Mot de passe requis (min. 8 caractères)'
    if (password !== confirmPassword) errs.confirmPassword = 'Les mots de passe ne correspondent pas'
    if (!terms) errs.terms = 'Vous devez accepter les CGV pour continuer'

    return errs
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError('')

    const fd = new FormData(e.currentTarget)
    const errs = validate(fd)

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      scrollToFirstError()
      return
    }
    setFieldErrors({})
    setLoading(true)

    try {
      const res = await fetch(`${API}/api/auth/register/provider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: fd.get('firstName'),
          lastName: fd.get('lastName'),
          email: fd.get('email'),
          phone: fd.get('phone') || undefined,
          bio: fd.get('bio') || undefined,
          password: fd.get('password'),
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message ?? 'Erreur lors de l\'inscription')
      }

      setSuccess(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-5xl mb-5">🚀</div>
        <h1 className="text-2xl font-bold text-text-primary mb-3">Bienvenue sur Velink !</h1>
        <p className="text-text-secondary text-sm mb-2">
          Votre profil prestataire a été créé. Téléchargez l'application mobile pour recevoir vos premières notifications de mission.
        </p>
        <p className="text-text-muted text-xs mb-6">
          (Application mobile disponible prochainement sur l'App Store et Google Play)
        </p>
        <Link to="/connexion" className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
          Se connecter →
        </Link>
      </div>
    )
  }

  return (
    <>
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-accent/20 border border-accent/30 flex items-center justify-center">
            <span className="text-accent font-bold text-lg">V</span>
          </div>
          <span className="font-semibold text-text-primary text-xl tracking-tight">Velink</span>
        </div>
      </div>

      <div className="glass-card p-8">
        <div className="mb-7">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 border border-success/20 text-success text-xs font-medium mb-3">
            🎯 Compte Prestataire
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Devenir prestataire</h1>
          <p className="text-text-secondary text-sm mt-1.5">
            Déjà inscrit ?{' '}
            <Link to="/connexion" className="text-accent hover:text-accent-hover transition-colors">
              Se connecter
            </Link>
            {' · '}
            <Link to="/inscription/entreprise" className="text-text-muted hover:text-text-secondary transition-colors">
              Je suis une entreprise
            </Link>
          </p>
        </div>

        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Prénom + Nom */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="glass-label">
                Prénom <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                autoComplete="given-name"
                placeholder="Jean"
                className={`glass-input${fieldErrors.firstName ? ' error' : ''}`}
              />
              {fieldErrors.firstName && <p className="glass-field-error">{fieldErrors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="glass-label">
                Nom <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                autoComplete="family-name"
                placeholder="Dupont"
                className={`glass-input${fieldErrors.lastName ? ' error' : ''}`}
              />
              {fieldErrors.lastName && <p className="glass-field-error">{fieldErrors.lastName}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="glass-label">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              placeholder="jean.dupont@mail.fr"
              className={`glass-input${fieldErrors.email ? ' error' : ''}`}
            />
            {fieldErrors.email && <p className="glass-field-error">{fieldErrors.email}</p>}
          </div>

          {/* Téléphone */}
          <div>
            <label htmlFor="phone" className="glass-label">
              Téléphone{' '}
              <span className="text-text-muted font-normal">(optionnel)</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              autoComplete="tel"
              placeholder="+33 6 00 00 00 00"
              className="glass-input"
            />
          </div>

          {/* Bio */}
          <div>
            <label htmlFor="bio" className="glass-label">
              Présentation courte{' '}
              <span className="text-text-muted font-normal">(optionnel)</span>
            </label>
            <textarea
              id="bio"
              name="bio"
              rows={3}
              placeholder="Formateur Excel certifié, 5 ans d'expérience en événementiel…"
              className="glass-textarea"
            />
          </div>

          {/* Mot de passe */}
          <div>
            <label htmlFor="password" className="glass-label">
              Mot de passe <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="new-password"
                placeholder="Min. 8 caractères"
                className={`glass-input pr-11${fieldErrors.password ? ' error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors p-1"
                aria-label={showPwd ? 'Masquer' : 'Afficher'}
              >
                {showPwd ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {fieldErrors.password && <p className="glass-field-error">{fieldErrors.password}</p>}
          </div>

          {/* Confirmation */}
          <div>
            <label htmlFor="confirmPassword" className="glass-label">
              Confirmer le mot de passe <span className="text-danger">*</span>
            </label>
            <input
              type={showPwd ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              placeholder="••••••••"
              className={`glass-input${fieldErrors.confirmPassword ? ' error' : ''}`}
            />
            {fieldErrors.confirmPassword && (
              <p className="glass-field-error">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          {/* CGV */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="terms"
                className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 accent-accent flex-shrink-0"
              />
              <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                J'accepte les{' '}
                <Link to="/cgv" className="text-accent hover:text-accent-hover transition-colors">
                  conditions générales
                </Link>{' '}
                et la{' '}
                <Link to="/confidentialite" className="text-accent hover:text-accent-hover transition-colors">
                  politique de confidentialité
                </Link>
              </span>
            </label>
            {fieldErrors.terms && <p className="glass-field-error mt-1">{fieldErrors.terms}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="glass-btn-accent w-full py-3 text-sm rounded-xl mt-2"
          >
            {loading ? 'Création en cours…' : 'Créer mon profil prestataire →'}
          </button>
        </form>
      </div>
    </>
  )
}
